import { authFetch } from '../../../api/csrf';
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Calendar,
  Clock,
  Users,
  Edit,
  Trash2,
  Filter,
  Search,
  Save,
  X,
  FileText,
  Eye,
  MapPin,
  Download,
  Upload,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../../../components/common/Button/Button';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import PermissionGate from '../../../components/common/PermissionGate';
import {
  handleTextInputChange,
  handleSearchInputChange,
  handleNumberInputChange,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import { formatDate } from '../../../utils/stringUtils';
import fieldSalesService from '../../../api/crm/field-sales/fieldSalesService';
import RoutePlanFormModal from '../../../components/crm/RoutePlanFormModal';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import BulkUploadModal from '../../../components/common/BulkUploadModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';

const RoutePlans = ({ isTodayOnly = false }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isSalesExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE || user?.role === USER_ROLES.EXPORT_EXECUTIVE || user?.role === USER_ROLES.BUSINESS_RELATIONSHIP_MANAGER || user?.role === USER_ROLES.BUSINESS_DEVELOPMENT_EXECUTIVE;

  // Get today's date for date validation
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  // Initial filter values from URL (for persistence across refresh)
  const initialSearch = searchParams.get('search') || '';
  const initialAssignee = searchParams.get('assignee') || 'all';
  const initialStatus = searchParams.get('status') || 'all';
  const initialStartDate = searchParams.get('startDate') || '';
  const initialEndDate = searchParams.get('endDate') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialLimit = Number(searchParams.get('limit')) || 10;
  const initialSortBy = searchParams.get('sortBy') || null;
  const initialSortDir = searchParams.get('sortOrder') || 'asc';
  const isInitialTodayOnlyMount = useRef(true);
  const isInitialPageResetMount = useRef(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [startDate, setStartDate] = useState(isTodayOnly ? todayStr : initialStartDate);
  const [endDate, setEndDate] = useState(isTodayOnly ? todayStr : initialEndDate);
  const [selectedAssignee, setSelectedAssignee] = useState(initialAssignee);
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);

  // Sync date filters and clear other filters when isTodayOnly changes.
  // Skip on initial mount so URL-restored filters are preserved on refresh.
  useEffect(() => {
    if (isInitialTodayOnlyMount.current) {
      isInitialTodayOnlyMount.current = false;
      return;
    }
    // Reset filters
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedAssignee('all');
    setSelectedStatus('all');
    setCurrentPage(1);

    if (isTodayOnly) {
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else {
      setStartDate('');
      setEndDate('');
    }
  }, [isTodayOnly, todayStr]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanForEdit, setSelectedPlanForEdit] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const searchInputRef = useRef(null);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [customerSearchTerms, setCustomerSearchTerms] = useState({});

  // Selection and export state
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] =
    useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] =
    useState(false);
  const selectedExportWrapRef = useRef(null);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: initialSortBy, direction: initialSortDir });

  // Sync filter/pagination/sort state to URL for persistence across refresh
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const managedKeys = ['search', 'assignee', 'status', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      if (selectedAssignee && selectedAssignee !== 'all') params.set('assignee', selectedAssignee);
      if (selectedStatus && selectedStatus !== 'all') params.set('status', selectedStatus);
      // Date range is fixed to "today" in Visit Log mode, so don't persist it there
      if (!isTodayOnly) {
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
      }
      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortConfig.key) {
        params.set('sortBy', sortConfig.key);
        if (sortConfig.direction && sortConfig.direction !== 'asc') {
          params.set('sortOrder', sortConfig.direction);
        }
      }

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    selectedAssignee,
    selectedStatus,
    startDate,
    endDate,
    currentPage,
    itemsPerPage,
    sortConfig,
    isTodayOnly,
    setSearchParams,
  ]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      setSortConfig({ key: null, direction: 'asc' });
      return;
    }
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-400 inline" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="ml-1 h-3 w-3 text-agrosaf-green inline" />
      : <ArrowDown className="ml-1 h-3 w-3 text-agrosaf-green inline" />;
  };

  // React Query: Delete route plan mutation
  const deleteRoutePlanMutation = useMutation({
    mutationFn: async (routePlanId) => {
      const response = await fieldSalesService.deleteRoutePlan(routePlanId);
      if (response.success) {
        return response.message;
      } else {
        throw new Error(response.error || 'Failed to delete route plan');
      }
    },
    onSuccess: () => {
      toast.success('Route plan deleted successfully!');
      setShowDeleteConfirm(false);
      setPlanToDelete(null);
      // Invalidate and refetch route plans
      queryClient.invalidateQueries({ queryKey: ['fieldSales', 'routePlans'] });
    },
    onError: (error) => {
      
      toast.error(error.message || 'Failed to delete route plan');
      setShowDeleteConfirm(false);
      setPlanToDelete(null);
    }
  });

  // React Query: Bulk delete route plans mutation
  const bulkDeleteRoutePlanMutation = useMutation({
    mutationFn: async (ids) => {
      const response = await fieldSalesService.bulkDeleteRoutePlans(ids);
      if (response.success) {
        return response.message;
      } else {
        throw new Error(response.error || 'Failed to delete route plans');
      }
    },
    onSuccess: (message) => {
      toast.success(message || 'Route plans deleted successfully!');
      setShowBulkDeleteConfirm(false);
      setSelectedRows(new Set());
      queryClient.invalidateQueries({ queryKey: ['fieldSales', 'routePlans'] });
    },
    onError: (error) => {
      
      toast.error(error.message || 'Failed to delete route plans');
      setShowBulkDeleteConfirm(false);
    }
  });

  // React Query: Fetch assignees with search and limit
  const {
    data: assigneesData,
    isLoading: loadingAssignees,
    isError: assigneesError
  } = useQuery({
    queryKey: ['fieldSales', 'salesReps', assigneeSearchTerm],
    queryFn: async () => {
      const params = {
        limit: 10,
        roleFilter: 'sales',
        ...(assigneeSearchTerm && assigneeSearchTerm.length >= 2 ? { search: assigneeSearchTerm } : {})
      };
      const response = await fieldSalesService.getSalesReps(params);
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to load assignees');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to load assignees');
    }
  });

  const assignees = assigneesData || [];

  // React Query: Fetch leads with search and limit (only when modal is open)
  // Note: We'll use a combined search term for all customer dropdowns
  const [leadsSearchTerm, setLeadsSearchTerm] = useState('');

  const {
    data: leadsData,
    isLoading: loadingLeads,
    isError: leadsError
  } = useQuery({
    queryKey: ['fieldSales', 'leadsDropdown', leadsSearchTerm],
    queryFn: async () => {
      const params = {
        limit: 10,
        ...(leadsSearchTerm && leadsSearchTerm.length >= 2 ? { search: leadsSearchTerm } : {})
      };
      const response = await fieldSalesService.getLeadsForDropdown(params);
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to load leads');
      }
    },
    enabled: showCreateModal, // Only fetch when modal is open
    onError: (error) => {
      toast.error(error.message || 'Failed to load leads');
    }
  });

  const leads = leadsData || [];

  // React Query: Fetch customers for dropdown
  const [customersSearchTerm, setCustomersSearchTerm] = useState('');

  const {
    data: customersData,
    isLoading: loadingCustomers
  } = useQuery({
    queryKey: ['fieldSales', 'customersDropdown', customersSearchTerm],
    queryFn: async () => {
      const params = {
        limit: 10,
        ...(customersSearchTerm && customersSearchTerm.length >= 2 ? { search: customersSearchTerm } : {})
      };
      const response = await fieldSalesService.getCustomersForDropdown(params);
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to load customers');
      }
    },
    enabled: showCreateModal,
    onError: (error) => {
      toast.error(error.message || 'Failed to load customers');
    }
  });

  const customers = customersData || [];

  // Convert assignees to SearchableDropdown format
  const assigneeOptions = useMemo(() => {
    let finalAssignees = [...assignees];

    // In edit mode, if the current assignee is not in the list, add it
    if (showCreateModal && selectedPlanForEdit && selectedPlanForEdit.rep) {
      const currentRep = selectedPlanForEdit.rep;
      const repId = typeof currentRep === 'object' ? currentRep._id : currentRep;

      const exists = finalAssignees.some(a => a._id === repId);
      if (!exists && currentRep && typeof currentRep === 'object') {
        // Format if it's an object from the populated route plan
        const roleStr = currentRep.role || (currentRep.user?.role) || 'Unknown';
        const name = currentRep.name ||
          (currentRep.personalInfo ? `${currentRep.personalInfo.firstName} ${currentRep.personalInfo.lastName}` :
            (currentRep.user ? `${currentRep.user.firstName} ${currentRep.user.lastName}` : 'Unknown'));

        finalAssignees.unshift({
          ...currentRep,
          _id: repId,
          name: name,
          role: roleStr,
          department: currentRep.department || 'N/A'
        });
      }
    }

    return finalAssignees.map(assignee => {
      const roleStr = assignee.role ? assignee.role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown';
      return {
        id: assignee._id,
        displayText: `${assignee.name} [${roleStr}]`,
        searchText: `${assignee.name} ${assignee.email} ${assignee.employeeId || ''} ${assignee.role || ''} ${assignee.department || ''}`.toLowerCase(),
        title: assignee.name,
        raw: assignee,
        role: roleStr,
        department: assignee.department || 'N/A',
        renderOption: (opt) => (
          <div className="flex flex-col py-0.5">
            <span className="font-semibold text-gray-900">{opt.title}</span>
            <div className="flex items-center text-[11px] mt-0.5 space-x-2">
              <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-medium uppercase tracking-wider">{opt.role}</span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-500 italic">{opt.department}</span>
            </div>
          </div>
        )
      };
    });
  }, [assignees, showCreateModal, selectedPlanForEdit]);

  // Convert leads to SearchableDropdown format
  const leadOptions = useMemo(() => {
    return leads.map(lead => ({
      id: lead._id,
      displayText: lead.customerName,
      searchText: `${lead.customerName} ${lead.companyName || ''} ${lead.address || ''} ${lead.leadId || ''} ${lead.email || ''} ${lead.phone || ''} ${lead.alternatePhone || ''}`.toLowerCase(),
      title: lead.customerName,
      leadId: lead.leadId,
      phone: lead.phone,
      email: lead.email,
      company: lead.companyName,
      raw: lead,
      renderOption: (opt) => (
        <div className="flex flex-col py-0.5">
          <span className="font-semibold text-gray-900">{opt.title}</span>
          <div className="flex items-center text-[11px] mt-0.5 space-x-2">
            <span className="px-1.5 py-0.5 bg-green-50 text-green-600 rounded font-medium tracking-wider">{opt.leadId || 'N/A'}</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-500">{opt.phone || opt.email || opt.company || 'N/A'}</span>
          </div>
        </div>
      )
    }));
  }, [leads]);

  // Convert customers to SearchableDropdown format
  const customerOptions = useMemo(() => {
    return customers.map(cust => ({
      id: cust._id,
      displayText: cust.customerName,
      searchText: `${cust.customerName} ${cust.companyName || ''} ${cust.address || ''} ${cust.customerId || ''} ${cust.email || ''} ${cust.phone || ''}`.toLowerCase(),
      title: cust.customerName,
      customerId: cust.customerId,
      phone: cust.phone,
      email: cust.email,
      company: cust.companyName,
      raw: cust,
      renderOption: (opt) => (
        <div className="flex flex-col py-0.5">
          <span className="font-semibold text-gray-900">{opt.title}</span>
          <div className="flex items-center text-[11px] mt-0.5 space-x-2">
            <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-medium tracking-wider">{opt.customerId || 'N/A'}</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-500">{opt.phone || opt.email || opt.company || 'N/A'}</span>
          </div>
        </div>
      )
    }));
  }, [customers]);

  // React Query: Fetch route plans with filters and limit
  const {
    data: responseData,
    isLoading: loadingPlans,
    isError: plansError
  } = useQuery({
    queryKey: ['fieldSales', 'routePlans', startDate, endDate, selectedAssignee, selectedStatus, debouncedSearchTerm, currentPage, itemsPerPage],
    queryFn: async () => {
      const filters = {
        page: currentPage,
        limit: itemsPerPage,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(selectedAssignee !== 'all' ? { rep: selectedAssignee } : {}),
        ...(selectedStatus !== 'all' ? { status: selectedStatus } : {}),
        ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {})
      };
      const response = await fieldSalesService.getRoutePlans(filters);
      if (response.success && response.data) {
        return response;
      } else {
        throw new Error(response.error || 'Failed to load route plans');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to load route plans');
    }
  });

  const plans = responseData?.data || [];
  const pagination = responseData?.pagination || { total: 0, page: 1, limit: 10, pages: 1 };

  const sortedPlans = useMemo(() => {
    let sortableItems = [...plans];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'planStatus') {
          aValue = getPlanStatus(a);
          bValue = getPlanStatus(b);
        } else if (sortConfig.key === 'rep') {
          aValue = (a.rep?.name || (typeof a.rep === 'string' ? a.rep : '')).toString().toLowerCase();
          bValue = (b.rep?.name || (typeof b.rep === 'string' ? b.rep : '')).toString().toLowerCase();
        } else if (sortConfig.key === 'customers') {
          aValue = (a.customers?.[0]?.name || '').toLowerCase();
          bValue = (b.customers?.[0]?.name || '').toLowerCase();
        } else if (sortConfig.key === 'date') {
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
        } else if (sortConfig.key === 'estimatedTime') {
          aValue = Number(a.estimatedTime || 0);
          bValue = Number(b.estimatedTime || 0);
        } else if (sortConfig.key === 'createdAt') {
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = (bValue || '').toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [plans, sortConfig]);

  const baseExportFilters = useMemo(
    () => ({
      search: debouncedSearchTerm.trim() || undefined,
      rep: selectedAssignee !== 'all' ? selectedAssignee : undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    [
      debouncedSearchTerm,
      selectedAssignee,
      selectedStatus,
      startDate,
      endDate,
    ]
  );

  const currentPageIdsForExport = useMemo(
    () => sortedPlans.map((p) => String(p.id)).filter(Boolean),
    [sortedPlans]
  );

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageIdsForExport)
          : [];
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'crm-route-plans',
            format,
            scope,
            filters: {
              ...baseExportFilters,
              ...(overrideFilters != null ? overrideFilters : {}),
            },
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortConfig.key || 'date',
            sortOrder: sortConfig.direction || 'desc',
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `route-plans-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      sortConfig.key,
      sortConfig.direction,
      currentPageIdsForExport,
    ]
  );

  const handleRoutePlansSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected rows to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} plan(s) exported.`);
        setShowInlineSelectedFormats(false);
        setSelectedRows(new Set());
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleRoutePlansScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      const pageItems = sortedPlans;
      const totalFiltered = pagination?.total ?? 0;

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !pageItems.length) {
          toast.error('No data on current page to export.');
          return;
        }

        if (scope === 'filtered' && totalFiltered === 0) {
          toast.error('No route plans match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const rangeStart = meta?.startDate;
          const rangeEnd = meta?.endDate;
          if (!rangeStart || !rangeEnd) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate: rangeStart,
              endDate: rangeEnd,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [baseExportFilters, pagination?.total, sortedPlans, requestBackendExport]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Autofocus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Reset to first page when filters change (skip on initial mount so the
  // URL-restored page is preserved on refresh)
  useEffect(() => {
    if (isInitialPageResetMount.current) {
      isInitialPageResetMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [startDate, endDate, selectedAssignee, selectedStatus, debouncedSearchTerm]);

  const handleCreatePlan = () => {
    setSelectedPlanForEdit(null);
    setEditingPlanId(null);
    setShowCreateModal(true);
  };

  // Handle form save from RoutePlanFormModal
  const handleFormSave = (formData) => {
    // formData.rep now contains the _id of the representative
    const repId = formData.rep;

    if (!repId) {
      toast.error('Please select a valid assignee');
      return;
    }

    // Prepare route plan data - send only IDs
    const routePlanStatus = formData.status || 'scheduled';

    // When editing, update all customer statuses to match the route plan status
    const customerStatus = editingPlanId ? routePlanStatus : (routePlanStatus || 'scheduled');

    const routePlanData = {
      rep: {
        _id: repId
      },
      date: formData.date,
      customers: formData.customers.map((customer) => {
        const customerData = {
          name: customer.name,
          address: customer.address,
          time: customer.time,
          duration: customer.duration || 60,
          status: customerStatus,
          type: customer.type || 'lead'
        };

        // If it's a registered lead or customer, pass the ID
        if (customer._id) {
          if (customer.type === 'customer') {
            customerData.customerId = customer._id;
          } else {
            customerData.leadId = customer._id;
          }
          // The backend will also handle it via customer._id + customer.type
          customerData._id = customer._id;
        }

        return customerData;
      }),
      notes: formData.notes || '',
      status: routePlanStatus
    };

    // Trigger mutation
    if (editingPlanId) {
      updateRoutePlanMutation.mutate({ routePlanId: editingPlanId, routePlanData });
    } else {
      toast.loading('Saving route plan...');
      createRoutePlanMutation.mutate(routePlanData);
    }
  };

  // React Query: Create route plan mutation
  const createRoutePlanMutation = useMutation({
    mutationFn: async (routePlanData) => {
      const response = await fieldSalesService.createRoutePlan(routePlanData);
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to save route plan');
      }
    },
    onSuccess: () => {
      toast.success('Route plan saved successfully!');
      setShowCreateModal(false);
      setSelectedPlanForEdit(null);
      setEditingPlanId(null);
      setAssigneeSearchTerm(''); // Reset assignee search
      setLeadsSearchTerm(''); // Reset leads search
      // Invalidate and refetch route plans
      queryClient.invalidateQueries({ queryKey: ['fieldSales', 'routePlans'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save route plan');
    }
  });

  // React Query: Update route plan mutation
  const updateRoutePlanMutation = useMutation({
    mutationFn: async ({ routePlanId, routePlanData }) => {
      const response = await fieldSalesService.updateRoutePlan(routePlanId, routePlanData);
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to update route plan');
      }
    },
    onSuccess: () => {
      toast.success('Route plan updated successfully!');
      setShowCreateModal(false);
      setSelectedPlanForEdit(null);
      setEditingPlanId(null);
      setAssigneeSearchTerm(''); // Reset assignee search
      setLeadsSearchTerm(''); // Reset leads search
      // Invalidate and refetch route plans
      queryClient.invalidateQueries({ queryKey: ['fieldSales', 'routePlans'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update route plan');
    }
  });


  const handleEditPlan = (plan) => {
    setSelectedPlanForEdit(plan);
    setEditingPlanId(plan.id || plan._id || plan.routePlanId);
    setShowCreateModal(true);
  };

  const handleViewMap = (customer) => {
    setSelectedCustomer(customer);
    setShowMapModal(true);
  };

  const handleViewPlan = (plan) => {
    navigate(`/route_plan_details/${plan.id || plan._id || plan.routePlanId}`);
  };

  const handleDeletePlan = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePlan = () => {
    if (planToDelete) {
      // Use the correct ID property (could be id or _id depending on the data structure)
      const planId = planToDelete.id || planToDelete._id;
      if (planId) {
        deleteRoutePlanMutation.mutate(planId);
      }
    }
  };

  const handleApplyFilter = () => {
    // Filters are automatically applied via useEffect
    toast.success('Filters applied successfully!');
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    const isDateFiltered = isTodayOnly
      ? (startDate !== todayStr || endDate !== todayStr)
      : (startDate !== '' || endDate !== '');

    return debouncedSearchTerm.trim() !== '' ||
      selectedAssignee !== 'all' ||
      selectedStatus !== 'all' ||
      isDateFiltered;
  }, [debouncedSearchTerm, selectedAssignee, selectedStatus, startDate, endDate, isTodayOnly, todayStr]);

  const handleBulkDelete = () => {
    if (selectedRows.size > 0) {
      setShowBulkDeleteConfirm(true);
    }
  };

  const confirmBulkDelete = () => {
    const idsToDelete = Array.from(selectedRows);
    bulkDeleteRoutePlanMutation.mutate(idsToDelete);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedAssignee('all');
    setSelectedStatus('all');
    if (isTodayOnly) {
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  function getStatusColor(status) {
    switch (status) {
      case 'completed': return 'text-teal-700 bg-teal-50';
      case 'in-progress':
      case 'in-process': return 'text-blue-600 bg-blue-50';
      case 'scheduled': return 'text-gray-600 bg-gray-50';
      case 'cancelled': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  }

  function getPlanStatus(plan) {
    if (!plan.customers || plan.customers.length === 0) return plan.status || 'scheduled';
    let completedCount = 0;
    let cancelledCount = 0;
    let inProgressCount = 0;
    plan.customers.forEach(c => {
      const s = c.status || 'scheduled';
      if (s === 'completed') completedCount++;
      else if (s === 'cancelled') cancelledCount++;
      else if (s === 'in-progress' || s === 'in-process') inProgressCount++;
    });
    const total = plan.customers.length;
    if (completedCount === total) return 'completed';
    if (cancelledCount === total) return 'cancelled';
    if (completedCount + cancelledCount === total) return 'completed';
    if (completedCount > 0 || inProgressCount > 0) return 'in-progress';
    return plan.status || 'scheduled';
  };

  // Format status to human-readable format
  const formatStatus = (status) => {
    if (!status) return 'Scheduled';
    if (status === 'completed') return 'Visited';
    return status
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format time from 24-hour (HH:MM) to 12-hour (HH:MM AM/PM) format
  const formatTimeTo12Hour = (timeString) => {
    if (!timeString) return 'N/A';

    try {
      // Handle time string in format HH:MM or HH:MM:SS
      const timeParts = timeString.split(':');
      if (timeParts.length < 2) return timeString;

      const hours = parseInt(timeParts[0], 10);
      const minutes = timeParts[1];

      if (isNaN(hours) || hours < 0 || hours > 23) return timeString;

      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

      return `${displayHours}:${minutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  // Selection handlers
  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (items) => {
    if (items.length === 0) {
      setSelectedRows(new Set());
      return;
    }

    const allSelected = items.every(item => selectedRows.has(item.id));

    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (allSelected) {
        items.forEach(item => newSelection.delete(item.id));
      } else {
        items.forEach(item => newSelection.add(item.id));
      }
      return newSelection;
    });
  };

  // Filtering is now done on the backend via API

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{isTodayOnly ? "Visit Log" : "Route Plans"}</h1>
          <p className="text-gray-600">{isTodayOnly ? "View and manage today's field sales visits" : "Plan and manage field sales routes"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedRows.size > 0 && !isSalesExecutive && (
            <PermissionGate permission="field_sales" action="delete">
              <Button
                variant="danger"
                size="sm"
                onClick={handleBulkDelete}
                icon={<Trash2 className="h-4 w-4" />}
                loading={bulkDeleteRoutePlanMutation.isPending}
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="field_sales" action="read">
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportClick}
                icon={<Download className="h-4 w-4" />}
                disabled={exportLoading || loadingPlans}
                className={
                  selectedRows.size > 0
                    ? 'bg-blue-50 border-blue-200 text-blue-600'
                    : ''
                }
              >
                {selectedRows.size > 0
                  ? `Export Selected (${selectedRows.size})`
                  : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleRoutePlansSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="field_sales" action="create">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowBulkUploadModal(true)}
                icon={<Upload className="h-4 w-4" />}
              >
                Bulk Upload
              </Button>
              <Button
                variant="primary"
                onClick={handleCreatePlan}
                icon={<Plus className="h-4 w-4" />}
              >
                Create Plan
              </Button>
            </div>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by customer name..."
              value={searchTerm}
              onChange={(e) => handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search)}
              maxLength={FIELD_LIMITS.search}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-full text-base"
              autoComplete="off"
            />
          </div>
          {!isSalesExecutive && (
            <div className="w-full sm:w-auto min-w-[200px] max-w-[300px]">
              <SearchableDropdown
                options={assigneeOptions}
                value={selectedAssignee !== 'all' ? selectedAssignee : null}
                onChange={(selectedId) => {
                  setSelectedAssignee(selectedId || 'all');
                }}
                onSearch={(searchTerm) => {
                  setAssigneeSearchTerm(searchTerm);
                }}
                placeholder={loadingAssignees ? 'Loading...' : 'All Assignees'}
                loading={loadingAssignees}
                searchPlaceholder="Search by name or email..."
                minSearchLength={2}
                debounceMs={300}
                className="py-2.5 w-full"
              />
            </div>
          )}
          <div className="w-full sm:w-auto min-w-[150px]">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Visited</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          {!isTodayOnly && (
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onChange={({ startDate: newStart, endDate: newEnd }) => {
                setStartDate(newStart || '');
                setEndDate(newEnd || '');
              }}
              allowFutureDates={true}
            />
          )}
          {hasActiveFilters && (
            <Button
              variant="outline"
              icon={<X className="h-4 w-4" />}
              onClick={handleClearFilters}
              className="flex-shrink-0 whitespace-nowrap"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Route Plans Table View */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                    checked={plans.length > 0 && plans.every(plan => selectedRows.has(plan.id))}
                    onChange={() => handleSelectAll(plans)}
                  />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('customers')}>
                  <div className="flex items-center">
                    Customers
                    {renderSortIcon('customers')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('rep')}>
                  <div className="flex items-center">
                    Assigned To
                    {renderSortIcon('rep')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('date')}>
                  <div className="flex items-center">
                    Visit Date
                    {renderSortIcon('date')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('estimatedTime')}>
                  <div className="flex items-center">
                    Estimated Time
                    {renderSortIcon('estimatedTime')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('planStatus')}>
                  <div className="flex items-center">
                    Status
                    {renderSortIcon('planStatus')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('createdAt')}>
                  <div className="flex items-center">
                    Created
                    {renderSortIcon('createdAt')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loadingPlans ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p>Loading route plans...</p>
                  </td>
                </tr>
              ) : sortedPlans.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    <p>No route plans found. Create your first route plan!</p>
                  </td>
                </tr>
              ) : (
                sortedPlans.map(plan => (
                  <tr key={plan.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                        checked={selectedRows.has(plan.id)}
                        onChange={() => handleSelectRow(plan.id)}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        {plan.customers?.length > 0 ? (
                          <>
                            {plan.customers.slice(0, 2).map((customer, idx) => (
                              <div key={idx} className=" text-gray-900 truncate max-w-[150px] capitalize" title={customer.name}>
                                {customer.name}
                              </div>
                            ))}
                            {plan.customers.length > 2 && (
                              <div className="text-xs text-blue-600 font-medium cursor-pointer mt-1" onClick={() => handleViewPlan(plan)}>
                                + {plan.customers.length - 2} more customers
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">No customers</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 capitalize">{plan.rep?.name || (typeof plan.rep === 'string' ? plan.rep : 'Unknown')}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-900 font-medium">{formatDate(plan.date, { includeTime: false })}</span>
                        {plan.customers?.[0]?.time && (
                          <span className="text-xs text-gray-500">{formatTimeTo12Hour(plan.customers[0].time)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                        {Math.floor(plan.estimatedTime / 60)}h {plan.estimatedTime % 60}m
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(getPlanStatus(plan))}`}>
                        {formatStatus(getPlanStatus(plan))}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-500">
                        {plan.createdAt ? formatDate(plan.createdAt, { includeTime: true }) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewPlan(plan)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {!isSalesExecutive && (
                          <>
                            <PermissionGate permission="field_sales" action="update">
                              {getPlanStatus(plan) === 'scheduled' && (
                                <button
                                  onClick={() => handleEditPlan(plan)}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                            </PermissionGate>
                            {getPlanStatus(plan) !== 'in-progress' && (
                              <PermissionGate permission="field_sales" action="delete">
                                <button
                                  onClick={() => handleDeletePlan(plan)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {!loadingPlans && pagination.total > itemsPerPage && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <PaginationControls
              currentPage={currentPage}
              totalPages={pagination.pages}
              totalItems={pagination.total}
              limit={itemsPerPage}
              hasNextPage={pagination.hasNextPage}
              hasPrevPage={pagination.hasPrevPage}
              onPageChange={(page) => setCurrentPage(page)}
              onLimitChange={(limit) => {
                setItemsPerPage(limit);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </div>

      {/* Create/Edit Plan Modal */}
      <RoutePlanFormModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedPlanForEdit(null);
          setEditingPlanId(null);
          setAssigneeSearchTerm('');
          setLeadsSearchTerm('');
        }}
        mode={editingPlanId ? 'edit' : 'create'}
        initialData={selectedPlanForEdit}
        onSave={handleFormSave}
        repOptions={assigneeOptions}
        loadingReps={loadingAssignees}
        onRepSearch={(searchTerm) => setAssigneeSearchTerm(searchTerm)}
        leadOptions={leadOptions}
        loadingLeads={loadingLeads}
        onLeadSearch={(searchTerm) => setLeadsSearchTerm(searchTerm)}
        customerOptions={customerOptions}
        loadingCustomers={loadingCustomers}
        onCustomerSearch={(searchTerm) => setCustomersSearchTerm(searchTerm)}
        leads={[...leads, ...customers]}
        defaultDate={startDate || todayStr}
        isSaving={createRoutePlanMutation.isPending || updateRoutePlanMutation.isPending}
      />
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && planToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 !mt-0">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-3 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete the route plan assigned to{' '}
                <span className="font-semibold">
                  {planToDelete.rep?.name || (typeof planToDelete.rep === 'string' ? planToDelete.rep : 'Unknown')} on {formatDate(planToDelete.date, { includeTime: false })}
                </span>
                ?
              </p>

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setPlanToDelete(null);
                  }}
                  disabled={deleteRoutePlanMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={confirmDeletePlan}
                  disabled={deleteRoutePlanMutation.isPending}
                  icon={
                    deleteRoutePlanMutation.isPending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )
                  }
                >
                  {deleteRoutePlanMutation.isPending ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 !mt-0">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-3 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Confirm Bulk Deletion</h3>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete <span className="font-bold">{selectedRows.size}</span> selected route plans? This action cannot be undone.
              </p>

              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  disabled={bulkDeleteRoutePlanMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={confirmBulkDelete}
                  disabled={bulkDeleteRoutePlanMutation.isPending}
                  icon={
                    bulkDeleteRoutePlanMutation.isPending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )
                  }
                >
                  {bulkDeleteRoutePlanMutation.isPending ? 'Deleting...' : 'Delete Selected'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Route Plans"
        showDateRange
        scopedFormatExport={{
          onExport: handleRoutePlansScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['fieldSales', 'routePlans'] });
          toast.success('Route plans imported successfully!');
        }}
        config={{
          title: 'Bulk Upload Route Plans',
          subtitle: 'Import multiple route plans using a CSV file',
          templateEndpoint: '/crm/field-sales/route-plans/template',
          validateEndpoint: '/crm/field-sales/route-plans/validate-import',
          uploadEndpoint: '/crm/field-sales/route-plans/import',
          templateFilename: 'route_plan_import_template.csv',
          dataKey: 'data',
          maxRecords: 100,
          instructions: {
            download: [
              'Download the CSV template to see the required format.',
              'Keep the header row as is.'
            ],
            prepare: [
              'Employee ID: Unique ID of the sales representative.',
              'Customer/Lead ID: Unique ID (e.g. CUST-2026-0047) of the customer or lead.',
              'Address: Full address for the visit.',
              'Visit Date (YYYY-MM-DD): Date of the visit.',
              'Visit Time (HH:MM): Scheduled time in 24-hour format.',
              'Duration (Minutes): Expected duration of the visit.',
              'Notes: Any additional instructions for the plan.'
            ]
          }
        }}
      />
    </div>
  );
};

export default RoutePlans;
